import { ArrowUpDown, Plus } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'

import { useAuth } from '@/contexts/auth-context'
import { useForumUnread } from '@/contexts/forum-unread-context'
import { Role } from '@/enums/role.enum'

import ChannelItem from './channel-item'

import { Channel } from '@/types/group-channel.type'

interface UngroupedChannelsProps {
  channels: Channel[]
  activeChannelId: string | null
  onSelectChannel: (channel: Channel) => void
  onAddChannel: () => void
  onEditChannel: (channel: Channel) => void
  onDeleteChannel: (channel: Channel) => void
  onReorderChannels?: () => void
}

export default function UngroupedChannels({
  channels,
  activeChannelId,
  onSelectChannel,
  onAddChannel,
  onEditChannel,
  onDeleteChannel,
  onReorderChannels,
}: UngroupedChannelsProps) {
  const { user } = useAuth()
  const isAdmin = user?.roles.includes(Role.ADMIN)
  const { getUnread, resetChannel } = useForumUnread()

  if (channels.length === 0) {
    return null
  }

  return (
    <div className="mb-4 border-b border-sidebar-border pb-4">
      <div className="flex items-center justify-between px-3 sm:px-4 py-1 sm:py-1.5 mb-2 group">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">General Channels</h3>
        {isAdmin && (
          <div className="flex items-center gap-1">
            {onReorderChannels && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onReorderChannels}
                className="h-5 w-5 p-0 hover:bg-sidebar-accent opacity-0 group-hover:opacity-100 transition-opacity"
                title="Sắp xếp kênh"
              >
                <ArrowUpDown className="h-3 w-3" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onAddChannel}
              className="h-5 w-5 p-0 hover:bg-sidebar-accent opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      <ul>
        {channels.map(channel => (
          <ChannelItem
            key={channel.id}
            channel={channel}
            isActive={channel.id === activeChannelId}
            onSelect={() => {
              onSelectChannel(channel)
              resetChannel(channel.id)
            }}
            onEdit={() => onEditChannel(channel)}
            onDelete={() => onDeleteChannel(channel)}
            unreadCount={getUnread(channel.id)}
          />
        ))}
      </ul>
    </div>
  )
}
